/**
 * 계약 및 청구 주체 정보 수정 컴포넌트
 * 계약 및 청구 주체의 정보를 수정하는 컴포넌트입니다.
 * 구분 선택에 따라 해당없음/사업자/개인의 다른 UI를 제공합니다.
 */

import _ from "lodash";
import { useCallback, useEffect, useState } from "react";
import { ContractManagaDetail, DetailTotalData } from "src/api/contract/contract-types";
import { MbOrgDetail } from "src/api/organization/org-types";
import { BaseButton, BaseRadio, BaseTooltip } from "src/components";
import BaseNewTabLink from "src/components/BaseNewTabLink";
import SearchOrgPopup from "src/pages/commonPopup/SearchOrgPopup";
import { PagePath } from "src/pages/product/details";
import { formatPhoneNumber } from "src/utils/common-util";
import BaseSection from "../../../../components/layout/BaseSection";
import { default as useContractApi } from "../../hooks/useContractApi";
import ContractImageUpload from "../ContractImageUpload";
import { BizLocationType } from "src/pages/organization/org-types";

/**
 * 계약 및 청구 주체 정보 수정 컴포넌트의 props 인터페이스
 */
interface ContractBillingSubjectEditProp {
  detailData: DetailTotalData; // 상품 상세 조회 정보
  setData: Function; // 데이터 업데이트 함수
  setEditable: any; // 편집 가능 상태 설정 함수
  saveFlags: boolean; // 저장 플래그
  setSaveFlags: any; // 저장 플래그 설정 함수
  popupState: boolean; // 팝업 상태
  setPopupState: any; // 팝업 상태 설정 함수
  setCommonModals: any; // 공통 모달 설정 함수
  setEditConfrim: any; // 편집 확인 설정 함수
}

/**
 * 계약 및 청구 주체 정보 수정 컴포넌트
 */
const ContractBillingSubjectEdit = ({
  detailData,
  setData,
  setEditable,
  saveFlags,
  setSaveFlags,
  setCommonModals,
  setPopupState,
  setEditConfrim,
}: ContractBillingSubjectEditProp) => {
  const { editContractManageData, getOrganizationData } = useContractApi();
  const { contractManage, contractOrganization } = detailData;

  // 상태 관리
  const [classification, setClassification] = useState<"해당없음" | "개인" | "사업자">("해당없음"); // 구분 상태
  const [organization, setOrganization] = useState<MbOrgDetail>(); // 사업자 정보 상태
  const [individualInfo, setIndividualInfo] = useState<MbOrgDetail>(); // 개인 정보 상태
  const [editContractManage, setEditContractManage] = useState<ContractManagaDetail>(); // 수정 중인 계약 관리 정보
  const [isOrgPop, setIsOrgPop] = useState(false); // 조직 검색 팝업 표시 여부

  // 구분 옵션
  const classificationOptions = [
    { value: "해당없음", label: "해당없음" },
    { value: "사업자", label: "사업자" },
    { value: "개인", label: "개인" },
  ] as const;

  /**
   * 데이터 초기화 함수
   */
  const initDatas = useCallback(async () => {
    console.log("1 initDatas contractManage", contractManage);
    console.log("2 initDatas contractOrganization", contractOrganization);
    if (contractManage) {
      setEditContractManage(_.cloneDeep(contractManage));
      // const proofs = contractOrganization?.mediaList!.filter((media: any) => media.type === "PROOF");

      // setProof(proofs);

      if (contractManage?.mbOrganizationId === "0" || !contractManage?.mbOrganizationId) {
        setClassification("해당없음");
      } else {
        // classifications 확인하여 개인/사업자 판별
        if (contractOrganization?.organization?.classifications && contractOrganization?.organization?.classifications.includes("INDIVIDUAL")) {
          setClassification("개인");
          setIndividualInfo(contractOrganization);
        } else {
          setClassification("사업자");
          setOrganization(contractOrganization);
        }
      }
    }
  }, [contractManage, contractOrganization]);

  // 컴포넌트 마운트 시 데이터 초기화
  useEffect(() => {
    initDatas();
  }, [initDatas]);

  /**
   * 구분 변경 처리
   */
  const onChangeClassification = useCallback(
    (value: "해당없음" | "개인" | "사업자") => {
      setClassification(value);

      if (value === "해당없음") {
        // mbOrganizationId를 0으로 설정
        const temp = _.cloneDeep(editContractManage);
        if (temp) {
          temp.mbOrganizationId = "0";
          setEditContractManage(temp);
        }
      }
    },
    [editContractManage],
  );

  /**
   * 조직 변경 처리 (계약자 선택 시 사업자/개인 선택 시 조직 선택)
   */
  const onChangeOrganization = useCallback(
    async (org: any) => {
      setIsOrgPop(false);
      try {
        const response = await getOrganizationData(Number(org.id));
        console.log("response", response);

        const temp = _.cloneDeep(editContractManage);
        if (temp) {
          temp.mbOrganizationId = Number(response?.organization?.id);
          setEditContractManage(temp);
        }

        // classifications 확인하여 개인/사업자 판별
        if (response?.organization?.classifications && response?.organization?.classifications.includes("INDIVIDUAL")) {
          setClassification("개인");
          setIndividualInfo({ mediaList: response?.proofs, organization: response?.organization! });
        } else {
          setClassification("사업자");
          setOrganization({ mediaList: response?.proofs, organization: response?.organization! });
        }
      } catch (error) {
        console.error("조직 정보 조회 실패:", error);
      }
    },
    [editContractManage, getOrganizationData],
  );

  /**
   * 계약 관리 데이터 저장
   */
  const onSaveContractManageData = useCallback(async () => {
    // 필수값 검증
    if (!classification) {
      setCommonModals({
        message: "구분을 선택해 주세요.",
        title: "필수값 확인",
      });
      setPopupState(true);
      setSaveFlags(false);
      setEditConfrim(false);
      return;
    }

    if ((classification === "사업자" || classification === "개인") && editContractManage?.mbOrganizationId === "0") {
      setCommonModals({
        message: "계약자 정보를 조회하여 선택해 주세요.",
        title: "계약자 정보 확인",
      });
      setPopupState(true);
      setSaveFlags(false);
      setEditConfrim(false);
      return;
    }

    try {
      const result: any = await editContractManageData(contractManage!, editContractManage!);

      if (result.response.status! > 199 && result.response.status! < 300) {
        setCommonModals({
          message: "저장 되었습니다.",
          title: "계약 및 청구 주체 정보 변경",
        });
        setPopupState(true);
        setEditConfrim(false);
        setSaveFlags(false);
        setEditable(false);
        setData();
        return;
      }

      if (result.validationPass === false) {
        setCommonModals({
          message: result.message,
          title: "계약 및 청구 주체 정보 변경",
        });
        setPopupState(true);
        setSaveFlags(false);
        setEditConfrim(false);
        return;
      }
    } catch (error) {
      console.error("저장 실패:", error);
      setCommonModals({
        message: "저장 중 오류가 발생했습니다.",
        title: "오류",
      });
      setPopupState(true);
      setSaveFlags(false);
      setEditConfrim(false);
    }
  }, [
    classification,
    editContractManageData,
    contractManage,
    editContractManage,
    setCommonModals,
    setPopupState,
    setSaveFlags,
    setEditable,
    setData,
    setEditConfrim,
  ]);

  // 저장 플래그 변경 시에만 반응
  useEffect(() => {
    if (saveFlags === true) {
      onSaveContractManageData();
    } else {
      // 취소 클릭 시
      // initDatas();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveFlags, setSaveFlags]);

  return (
    <>
      <div className="flex-center">
        <BaseSection
          titleChildren={
            <div className="flex-center">
              <p className="">구분</p>
              <BaseTooltip touchIcon={"QUESTION"} className="ml4 mt1">
                <ul className="base-list">
                  <li style={{ color: "#191919" }}>세금계산서 발행 시 '계약자' 메뉴에서 사업자 정보 등록은 필수입니다.</li>
                  <li style={{ color: "#191919" }}>현금영수증 발행 시 '계약자' 메뉴에서 개인 정보 등록은 필수입니다.</li>
                </ul>
              </BaseTooltip>
            </div>
          }
          children={
            <div className="flex-row flex-center-start">
              {classificationOptions.map((option) => (
                <div key={option.value} className="flex-row flex-center-start mr20">
                  <BaseRadio
                    id={`classification-${option.value}`}
                    name={"classification"}
                    label={option.label}
                    checked={classification === option.value}
                    onChange={() => onChangeClassification(option.value)}
                  />
                </div>
              ))}
            </div>
          }
        />
        {classification === "개인" && (
          <BaseSection
            title={"이름"}
            children={
              <div className="flex-row flex-center-start">
                <BaseButton title="조회" onClick={() => setIsOrgPop(true)} className="color-white" />
                {individualInfo && (
                  <div className="flex-row flex-center-start minmax300">
                    <BaseNewTabLink
                      path={PagePath.organization.detail.replace(":id", individualInfo?.organization?.id?.toString() || "")}
                      value={individualInfo?.organization?.ceoName || ""}
                      ellipsis
                      className="ml16 mr4"
                    />
                    {individualInfo?.organization?.ceoName && (
                      <div
                        className="delete-btn-x"
                        onClick={() => {
                          setIndividualInfo(undefined);
                          setEditContractManage({ ...editContractManage, mbOrganizationId: "0" });
                        }}
                      ></div>
                    )}
                  </div>
                )}
              </div>
            }
          />
        )}
        {classification === "사업자" && (
          <BaseSection
            title={"법인명/상호"}
            children={
              <div className="flex-row flex-center-start">
                <BaseButton title="조회" onClick={() => setIsOrgPop(true)} className="color-white" />
                {organization && (
                  <div className="flex-row flex-center-start minmax300">
                    <BaseNewTabLink
                      path={PagePath.organization.detail.replace(":id", organization?.organization?.id?.toString() || "")}
                      value={organization?.organization?.name || ""}
                      ellipsis
                      className="ml16 mr4"
                    />
                    {organization?.organization?.name && (
                      <div
                        className="delete-btn-x"
                        onClick={() => {
                          setOrganization(undefined);
                          setEditContractManage({ ...editContractManage, mbOrganizationId: "0" });
                        }}
                      ></div>
                    )}
                  </div>
                )}
              </div>
            }
          />
        )}
      </div>

      {/* 개인 선택 시 */}
      {classification === "개인" && (
        <>
          <div className="flex-center">
            <BaseSection title={"국가"} value={individualInfo?.organization?.bizLocationType === BizLocationType.OVERSEAS ? "해외" : "국내"} />
            <BaseSection title={"주소"} value={individualInfo?.organization?.address || "-"} />
          </div>
          <div className="flex-center">
            <BaseSection title={"휴대폰"} value={formatPhoneNumber(individualInfo?.organization?.phoneNumber || "")} />
            <BaseSection title={"이메일"} value={individualInfo?.organization?.email || "-"} />
          </div>
          <div className="flex-start">

            <BaseSection
              title={"증빙 서류 첨부"}
              children={individualInfo && <ContractImageUpload isOnlyView images={individualInfo?.mediaList} type={"PROOF"} />}
            />
          </div>
        </>
      )}

      {/* 사업자 선택 시 */}
      {classification === "사업자" && (
        <>
          <div className="flex-center">
            <BaseSection title={"국가"} value={organization?.organization?.bizLocationType === BizLocationType.OVERSEAS ? "해외" : "국내"} />
            <BaseSection
              title={"사업자 유형"}
              value={organization?.organization?.bizType ? (organization?.organization?.bizType === "CORP" ? "법인사업자" : "일반사업자") : "-"}
            />

          </div>
          <div className="flex-center">
            <BaseSection title={"대표자/성명"} value={organization?.organization?.ceoName || "-"} />
            <BaseSection title={"사업자 등록번호"} value={organization?.organization?.bizRegNo || "-"} />

          </div>
          <BaseSection title={"사업장 주소"} value={organization?.organization?.address || "-"} />
          <div className="flex-center">

            <BaseSection
              title={"사업자 등록증 첨부"}
              children={organization && <ContractImageUpload isOnlyView images={organization?.mediaList} type={"PROOF"} />}
            />
          </div>
        </>
      )}

      {/* 계약자 선택 팝업 */}
      {isOrgPop && (
        <SearchOrgPopup
          isOpen={isOrgPop}
          onClose={() => setIsOrgPop(false)}
          onClick={onChangeOrganization}
          defaultSearchOption={{
            classification: classification === "개인" ? "INDIVIDUAL" : "GENERAL_BIZ",
          }}
          //개인일때는 사업자 선택 못하게 비활성화
          disabledClassification={classification === "개인" ? "GENERAL_BIZ" : "INDIVIDUAL"}
        />
      )}
    </>
  );
};

export default ContractBillingSubjectEdit;
